import type { PricingModalOrigin } from '@wegic/price-ui';

export const CAMPAIGN_POPUP_ORIGIN = 'popup' as PricingModalOrigin;
const WINBACK_EMAIL_ORIGIN = 'winback_email';
const WINBACK_EMAIL_SOURCES = new Set([
  'CRM_cta_1_299trial',
  'CRM_cta_1_299generated',
  'CRM_cta_1_cloud',
  'CRM_cta_1_comment',
  'CRM_cta_1_siteaudit',
  'CRM_cta_1_3daysleft',
  'CRM_cta_1_expired',
]);

export interface PricingModalTrackingContextOptions {
  /** 没有显式来源时使用的兜底 rights 值 */
  fallbackOrigin?: PricingModalOrigin;
  /** pay_success 需要同时上报 origin，其他事件只需要 rights */
  includeOrigin?: boolean;
  origin?: string | null;
  popoupType?: string | null;
}

export interface PricingModalTrackingPayloadInput extends PricingModalTrackingContextOptions {
  source?: string | null;
}

/** 解析邮件归因：历史召回邮件归一化，动态促销邮件保留原始 source。 */
function resolveCrmEmailOrigin(source?: string | null) {
  if (!source?.startsWith('CRM_')) {
    return null;
  }

  return WINBACK_EMAIL_SOURCES.has(source) ? WINBACK_EMAIL_ORIGIN : source;
}

/** Header 入口需要保留页面入口信息，优先级高于外部 CRM 来源。 */
function isHeaderTrackingOrigin(origin?: string | null) {
  return !!origin && /_header_(?:upgrade|credits)$/.test(origin);
}

/**
 * 解析最终的埋点来源：Header > CRM 邮件 > 显式 origin > fallbackOrigin > null。
 */
function resolveTrackingOrigin({
  origin,
  fallbackOrigin,
  source,
}: Pick<PricingModalTrackingPayloadInput, 'origin' | 'fallbackOrigin' | 'source'>) {
  if (isHeaderTrackingOrigin(origin)) {
    return origin;
  }

  const crmEmailOrigin = resolveCrmEmailOrigin(source);
  if (crmEmailOrigin) {
    return crmEmailOrigin;
  }

  return origin || fallbackOrigin || null;
}

/** 单独解析一个来源值，供不需要读取存储的调用方使用。 */
export function getResolvedPricingTrackingOrigin(origin?: string | null, source?: string | null) {
  return resolveTrackingOrigin({ origin, source });
}

/** 根据来源配置生成 rights、origin 和 popup 类型等公共埋点参数。 */
export function getPricingModalTrackingPayload(input: PricingModalTrackingPayloadInput = {}) {
  const rights = resolveTrackingOrigin(input);
  const payload: Record<string, string | null> = { rights };

  if (input.includeOrigin) {
    payload.origin = rights;
  }

  if (rights === CAMPAIGN_POPUP_ORIGIN && input.popoupType) {
    payload.popoup_type = input.popoupType;
  }

  return payload;
}
